<script setup lang="ts">
/**
 * A growing textarea with @-mentions: „@“ at a word start opens a people list
 * (`candidates`, filtered by name, initials and short name), arrow keys walk
 * it, Enter or Tab picks, Escape closes. The picked people ride NEXT TO the
 * text as `NxMention` ranges (`v-model:mentions`) — editing through a name
 * drops it, editing before it shifts it. The engine is `useMentionComposer`.
 *
 * Ctrl/Cmd+Enter emits `submit`; Escape with the list closed emits `cancel`
 * (an inline editor closes on it). Setting `modelValue` from outside to a
 * text the field does not hold resets the mentions to `mentions`.
 */
import { computed, nextTick, onMounted, ref, useId, watch } from 'vue';
import NxAvatar from '../Avatar/NxAvatar.vue';
import type { NxMention, NxMentionCandidate } from '../Mentions/types';
import { useMentionComposer } from '../Mentions/useMentionComposer';

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    mentions?: readonly NxMention[];
    candidates?: readonly NxMentionCandidate[];
    placeholder?: string;
    disabled?: boolean;
    /** Visible rows when empty. */
    rows?: number;
    /** Logical px the field grows to before it scrolls. */
    maxHeight?: number;
    /** Most people in the list. */
    maxCandidates?: number;
    /** Where the list opens. */
    pickerPlacement?: 'below' | 'above';
    /** Accessible name of the field (default: the placeholder names it). */
    label?: string;
    /** Accessible name of the people list. */
    mentionsLabel?: string;
  }>(),
  {
    modelValue: '',
    mentions: () => [],
    candidates: () => [],
    placeholder: '',
    disabled: false,
    rows: 2,
    maxHeight: 240,
    maxCandidates: 6,
    pickerPlacement: 'below',
    label: undefined,
    mentionsLabel: 'Personen',
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'update:mentions': [mentions: NxMention[]];
  /** Ctrl/Cmd+Enter. */
  submit: [];
  /** Escape while no list is open. */
  cancel: [];
}>();

const listId = useId();
const field = ref<HTMLTextAreaElement | null>(null);

const composer = useMentionComposer({
  candidates: () => [...props.candidates],
  maxCandidates: props.maxCandidates,
});
const { body, candidates: people, pickerIndex } = composer;
composer.reset(props.modelValue, [...props.mentions]);

const open = computed(() => composer.showPicker.value && people.value.length > 0);

watch(
  () => props.modelValue,
  (v) => {
    if (v === body.value) return;
    composer.reset(v, [...props.mentions]);
    void nextTick(resize);
  }
);
watch(
  () => props.mentions,
  (list) => {
    // Only foreign lists: our own emit comes back as the same content.
    if (sameMentions(list, composer.mentions.value)) return;
    composer.mentions.value = list.map((m) => ({ ...m }));
  }
);

function sameMentions(a: readonly NxMention[], b: readonly NxMention[]) {
  return (
    a.length === b.length &&
    a.every(
      (m, i) => m.accountId === b[i]!.accountId && m.start === b[i]!.start && m.end === b[i]!.end
    )
  );
}

function publish() {
  emit('update:modelValue', body.value);
  emit(
    'update:mentions',
    composer.mentions.value.map((m) => ({ ...m }))
  );
}

function resize() {
  const el = field.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, props.maxHeight)}px`;
}

function onInput(e: Event) {
  const el = e.target as HTMLTextAreaElement;
  composer.handleInput(el.value, el.selectionStart ?? el.value.length);
  publish();
  resize();
}

/** Re-reads the caret after a click or a sideways arrow. */
function detect() {
  const el = field.value;
  if (!el) return;
  composer.handleInput(el.value, el.selectionStart ?? el.value.length);
}

/** Writes the composer's text back into the field and puts the caret at `caret`. */
function applyCaret(caret: number) {
  publish();
  void nextTick(() => {
    const el = field.value;
    if (!el) return;
    if (el.value !== body.value) el.value = body.value;
    el.focus();
    el.setSelectionRange(caret, caret);
    resize();
  });
}

function pick(candidate: NxMentionCandidate) {
  const caret = composer.applyCandidate(candidate);
  if (caret !== null) applyCaret(caret);
}

function onKeydown(e: KeyboardEvent) {
  if (e.isComposing) return;
  if (open.value) {
    const handled = composer.handleKeydown(e);
    if (typeof handled === 'number') {
      applyCaret(handled);
      return;
    }
    if (handled) return;
  }
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    emit('submit');
  } else if (e.key === 'Escape' && !open.value) {
    emit('cancel');
  }
}

function onKeyup(e: KeyboardEvent) {
  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') detect();
}

onMounted(resize);

defineExpose({
  focus: () => field.value?.focus(),
  /** Empties the field and forgets the mentions. */
  clear: () => {
    composer.reset('');
    publish();
    void nextTick(resize);
  },
});
</script>

<template>
  <div class="nx-mention-textarea" :class="[`is-${pickerPlacement}`, { 'is-disabled': disabled }]">
    <textarea
      ref="field"
      class="nx-mention-textarea__field"
      :value="body"
      :rows="rows"
      :placeholder="placeholder"
      :disabled="disabled"
      :aria-label="label"
      aria-autocomplete="list"
      :aria-controls="open ? listId : undefined"
      :aria-activedescendant="open ? `${listId}-${pickerIndex}` : undefined"
      data-testid="nx-mention-textarea"
      @input="onInput"
      @keydown="onKeydown"
      @keyup="onKeyup"
      @click="detect"
      @blur="composer.closePicker()"
    />
    <ul
      v-if="open"
      :id="listId"
      class="nx-mention-textarea__picker"
      role="listbox"
      :aria-label="mentionsLabel"
    >
      <li
        v-for="(person, index) in people"
        :id="`${listId}-${index}`"
        :key="person.id"
        class="nx-mention-textarea__option"
        :class="{ 'is-active': index === pickerIndex }"
        role="option"
        :aria-selected="index === pickerIndex"
        @mousedown.prevent
        @mouseenter="pickerIndex = index"
        @click="pick(person)"
      >
        <NxAvatar :name="person.name" :initials="person.initials" :size="22" decorative />
        <span class="nx-mention-textarea__name">{{ person.name }}</span>
        <span v-if="person.shortName" class="nx-mention-textarea__short">
          {{ person.shortName }}
        </span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.nx-mention-textarea {
  position: relative;
  display: flex;
  min-width: 0;
}
.nx-mention-textarea__field {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  resize: none;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-input-bg);
  color: var(--nxt-input-fg);
  font-family: var(--nxt-font);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  scrollbar-width: thin;
  scrollbar-color: var(--nxt-border-strong) transparent;
  transition:
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-mention-textarea__field:hover:not(:focus):not(:disabled) {
  border-color: var(--nxt-input-border-hover);
}
.nx-mention-textarea__field:focus {
  outline: none;
  border-color: var(--nxt-input-border-focus);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);
}
.nx-mention-textarea__field::placeholder {
  color: var(--nxt-input-placeholder);
}
.nx-mention-textarea__field:disabled {
  cursor: not-allowed;
  background: var(--nxt-input-bg-disabled);
  color: var(--nxt-input-fg-disabled);
  border-color: var(--nxt-input-border-disabled, var(--nxt-border));
}

.nx-mention-textarea__picker {
  position: absolute;
  inset-inline: 0;
  z-index: var(--nxt-z-dropdown);
  max-height: var(--nxt-area-size-lg);
  margin: 0;
  padding: var(--nxt-space-2xs);
  overflow-y: auto;
  list-style: none;
  font-size: inherit;
  background: var(--nxt-popover-bg);
  color: var(--nxt-popover-fg);
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-md);
  box-shadow: var(--nxt-popover-shadow);
}
.nx-mention-textarea.is-below .nx-mention-textarea__picker {
  inset-block-start: calc(100% + var(--nxt-space-2xs));
}
.nx-mention-textarea.is-above .nx-mention-textarea__picker {
  inset-block-end: calc(100% + var(--nxt-space-2xs));
}
.nx-mention-textarea__option {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: calc(var(--nxt-space-xs) * 0.75) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  font-size: var(--nxt-font-size-sm);
  cursor: pointer;
}
.nx-mention-textarea__option.is-active {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-mention-textarea__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-mention-textarea__short {
  margin-inline-start: auto;
  font-family: var(--nxt-font-mono);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
@media (prefers-reduced-motion: reduce) {
  .nx-mention-textarea__field {
    transition: none;
  }
}
</style>
